import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import Login from './components/Login';
import Layout from './components/Layout';
import Members from './components/Members';
import Households from './components/Households';
import Finance from './components/Finance';
import Financials from './components/Financials';
import Groups from './components/Groups';
import Events from './components/Events';
import Comms from './components/Comms';
import Checkin from './components/Checkin';
import Content from './components/Content';

// Section -> route path. 'members' is the index route, so its path is '/'.
// 'cameras' is intentionally absent: declared in roles.js, not built yet.
const SECTION_PATH = {
  members: '/',
  households: '/households',
  finance: '/finance',
  financials: '/financials',
  groups: '/groups',
  events: '/events',
  comms: '/comms',
  checkin: '/checkin',
  content: '/content',
};

function RequireAuth({ children }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

/**
 * Hiding a nav link does not stop someone typing the URL, so each section is
 * guarded here too. ⚠️ Still UI-only — the API itself does not check roles.
 */
function RequireSection({ section, children }) {
  const { can } = useAuth();
  if (!can(section)) return <Navigate to="/" replace />;
  return children;
}

/**
 * Where '/' sends a role that cannot see Members. Lands on the first section
 * the role CAN see, so there is no redirect loop back to '/'.
 */
function HomeRedirect() {
  const { sections } = useAuth();
  const first = sections.find((s) => SECTION_PATH[s] && SECTION_PATH[s] !== '/');
  if (!first) return <NoSections />;
  return <Navigate to={SECTION_PATH[first]} replace />;
}

function NoSections() {
  const { user, logout } = useAuth();
  return (
    <div className="max-w-md mx-auto mt-16 text-center">
      <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
        No sections are available for your role
      </h1>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        Signed in as {user?.email} ({user?.role}). Ask an administrator to grant
        access.
      </p>
      <button
        onClick={logout}
        className="mt-4 text-sm text-brand-700 dark:text-brand-300 hover:underline"
      >
        Sign out
      </button>
    </div>
  );
}

export default function App() {
  const { isAuthenticated, can } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={can('members') ? <Members /> : <HomeRedirect />} />
        <Route
          path="households"
          element={
            <RequireSection section="households">
              <Households />
            </RequireSection>
          }
        />
        <Route
          path="finance"
          element={
            <RequireSection section="finance">
              <Finance />
            </RequireSection>
          }
        />
        <Route
          path="financials"
          element={
            <RequireSection section="financials">
              <Financials />
            </RequireSection>
          }
        />
        <Route
          path="groups"
          element={
            <RequireSection section="groups">
              <Groups />
            </RequireSection>
          }
        />
        <Route
          path="events"
          element={
            <RequireSection section="events">
              <Events />
            </RequireSection>
          }
        />
        <Route
          path="comms"
          element={
            <RequireSection section="comms">
              <Comms />
            </RequireSection>
          }
        />
        <Route
          path="checkin"
          element={
            <RequireSection section="checkin">
              <Checkin />
            </RequireSection>
          }
        />
        <Route
          path="content"
          element={
            <RequireSection section="content">
              <Content />
            </RequireSection>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
